<template>
  <div>
    <div class="container mt-5 d-flex plans-container" id="plans">
      <div class="my-auto w-100">
        <div class="text-center mb-4">
          <h6 class="section-label">BROADBAND PLANS</h6>
          <h1>Choose Your Plan</h1>
          <p class="section-sub">
            Unlimited data, no lock-in contracts. Choose the speed that fits
            your needs.
          </p>
        </div>

        <div class="text-center">
          <div class="plan-tabs">
            <button
              class="plan-tab"
              :class="{ active: activeTab === 'fiber' }"
              @click="switchTab('fiber')"
            >
              <i class="fas fa-ethernet"></i> Trident Fiber
            </button>
            <button
              class="plan-tab"
              :class="{ active: activeTab === 'wireless' }"
              @click="switchTab('wireless')"
            >
              <i class="fas fa-wifi"></i> Trident Wireless
            </button>
          </div>
        </div>

        <div class="row justify-content-center row-plans plans-grid">
          <div
            class="col-lg-4 col-md-4 col-sm-6 col-12 mb-4"
            v-for="(rate, key) in activePlans"
            :key="activeTab + '-' + key"
          >
            <div
              class="card plan-card"
              :class="{ 'plan-card--featured': rate.popular }"
            >
              <div class="ribbon ribbon-top-right" v-if="rate.popular">
                <span>POPULAR</span>
              </div>
              <div class="card-body text-center">
                <p class="plan-label">Up to</p>
                <h3 class="plan-speed">{{ rate.speed }}</h3>
              </div>
              <ul class="list-group list-group-flush">
                <li class="list-group-item">
                  <i class="fas fa-check"></i> <b>Unlimited</b> Data
                </li>
                <li class="list-group-item">
                  <i class="fas fa-check"></i> {{ activeTab === 'fiber' ? 'Fiber Connection' : 'Wireless Connection' }}
                </li>
                <li class="list-group-item">
                  <i class="fas fa-check"></i> No lock-in contract
                </li>
              </ul>
              <div class="card-body text-center mt-auto">
                <div class="plan-price">
                  <span class="plan-currency">PHP</span>
                  <span class="plan-amount">{{ rate.price }}</span>
                  <span class="plan-period">/mo</span>
                </div>
                <a
                  v-scroll-to="'#contact'"
                  href="#contact"
                  class="btn btn-lg plan-btn"
                  :class="rate.popular ? 'btn-secondary' : 'btn-outline-light'"
                >
                  Inquire Now
                </a>
              </div>
            </div>
          </div>
        </div>
        <div class="mt-3 text-end disclaimers">
          <i>* Speeds may vary depending on network conditions and location</i>
        </div>
      </div>
    </div>

    <div class="why-section">
      <div class="container">
        <div class="text-center mb-5">
          <h6 class="section-label">WHY TRIDENT</h6>
          <h2>Why Choose Us?</h2>
        </div>
        <div class="row">
          <div
            class="col-lg-4 col-md-6 col-12 mb-4"
            v-for="(item, i) in features"
            :key="i"
          >
            <div class="why-card">
              <div class="why-icon">
                <i :class="item.icon"></i>
              </div>
              <div>
                <h5>{{ item.title }}</h5>
                <p>{{ item.desc }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="footer-bg"></div>
    <div class="container d-flex contact-section" id="contact">
      <div class="m-auto w-100" style="max-width: 700px;">
        <div class="text-center mb-5">
          <h6 class="section-label">GET IN TOUCH</h6>
          <h1>Need a connection?</h1>
          <p class="section-sub">
            Fill out this form and we will get back to you within 24 hours.
          </p>
        </div>

        <form class="contact-form">
          <div class="mb-3">
            <label class="form-label">Full Name</label>
            <input type="text" class="form-control" placeholder="Juan Dela Cruz" />
          </div>
          <div class="row">
            <div class="mb-3 col-md-6">
              <label class="form-label">Email Address</label>
              <input type="email" class="form-control" placeholder="you@email.com" />
              <div class="form-text">
                We'll never share your email with anyone else.
              </div>
            </div>
            <div class="mb-3 col-md-6">
              <label class="form-label">Mobile Number</label>
              <input type="tel" class="form-control" placeholder="09XX XXX XXXX" />
            </div>
          </div>
          <div class="mb-3">
            <label class="form-label">Your Message</label>
            <textarea
              class="form-control"
              rows="5"
              placeholder="Tell us about your location and internet needs..."
            ></textarea>
          </div>
          <div class="mb-4">
            <div class="form-check">
              <input type="checkbox" class="form-check-input" id="termsCheck" />
              <label class="form-check-label" for="termsCheck">
                I accept the terms and conditions.
              </label>
            </div>
          </div>
          <button type="submit" class="btn btn-secondary btn-lg w-100">
            Send Message <i class="fas fa-paper-plane ms-2"></i>
          </button>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "Home",
  watch: {
    $route: function(oldVal, newVal) {
      switch (oldVal.hash === newVal.hash ? newVal.hash : oldVal.hash) {
        case "#plans":
          this.setPageTitle("Broadband Plans");
          break;
        case "#contact":
          this.setPageTitle("Contact Us");
          break;
        default:
          this.setPageTitle("Home");
          break;
      }
    }
  },
  computed: {
    activeTab() {
      return this.$store.state.activeTab;
    },
    activePlans() {
      return this.activeTab === 'fiber'
        ? this.$store.state.fiberPlans
        : this.$store.state.wirelessPlans;
    }
  },
  data() {
    return {
      tab_title: "Home",
      features: [
        {
          icon: "fas fa-broadcast-tower",
          title: "Wide Coverage",
          desc: "Over 15 relay stations covering rural and hard-to-reach areas across the region."
        },
        {
          icon: "fas fa-bolt",
          title: "Fast Fiber Speeds",
          desc: "Plans up to 400 Mbps with unlimited data. No throttling, no hidden caps."
        },
        {
          icon: "fas fa-hand-holding-usd",
          title: "Affordable Rates",
          desc: "Starting at just PHP 999/mo with no lock-in contracts or hidden fees."
        },
        {
          icon: "fas fa-tools",
          title: "Managed Service",
          desc: "We handle installation, maintenance, and troubleshooting so you don't have to."
        },
        {
          icon: "fas fa-headset",
          title: "24/7 Support",
          desc: "Our support team is always available to help resolve any connectivity issues."
        },
        {
          icon: "fas fa-shield-alt",
          title: "Reliable Connection",
          desc: "99.9% uptime guarantee with redundant relay infrastructure."
        }
      ]
    };
  },
  methods: {
    setPageTitle(name) {
      document.title = "Trident Network - " + name;
    },
    switchTab(tab) {
      this.$store.commit('setActiveTab', tab);
    }
  }
};
</script>

<style lang="scss" scoped>
.plans-grid {
  max-width: 900px;
  margin: 0 auto;
}

// ── Style 1: Segmented Control ──
.plan-tabs {
  display: inline-flex;
  background: #131516;
  border: 1px solid #242424;
  border-radius: 10px;
  padding: 4px;
  margin-bottom: 32px;
}

.plan-tab {
  background: transparent;
  border: none;
  color: #888;
  padding: 10px 32px;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.25s ease;

  i {
    margin-right: 6px;
  }

  &:hover {
    color: #ccc;
  }

  &.active {
    background: #00ffd0;
    color: #000;
    box-shadow: 0 2px 8px rgba(0, 255, 208, 0.2);
  }
}

.section-label {
  color: #00ffd0;
  font-size: 13px;
  letter-spacing: 3px;
  font-weight: 700;
  margin-bottom: 8px;
}

.section-sub {
  color: #b0b0b0;
  font-size: 18px;
  max-width: 600px;
  margin: 0 auto;
}

// Plan cards
.plan-card {
  background: #131516;
  border: 1px solid #242424;
  border-radius: 12px;
  padding: 25px 15px;
  height: 100%;
  display: flex;
  flex-direction: column;
  transition: transform 0.3s ease, border-color 0.3s ease;
  position: relative;
  overflow: hidden;

  &:hover {
    transform: translateY(-4px);
    border-color: #333;
  }

  &--featured {
    border-color: #00ffd0;
    background: linear-gradient(180deg, #131516 0%, #0f1a1d 100%);

    &:hover {
      border-color: #00ffd0;
    }
  }

  .list-group-item {
    background: transparent;
    border-color: #2d2d2d;
    color: #d0d0d0;
    font-size: 14px;
    padding: 8px 12px;

    i {
      color: #00ffd0;
      margin-right: 6px;
      font-size: 12px;
    }
  }
}

.plan-label {
  color: #999;
  font-size: 14px;
  text-transform: uppercase;
  letter-spacing: 2px;
  margin-bottom: 0;
}

.plan-speed {
  font-size: 32px;
  font-weight: 700;
  color: #fff;
  margin-bottom: 0;
}

.plan-price {
  margin-bottom: 16px;

  .plan-currency {
    font-size: 14px;
    color: #b0b0b0;
    vertical-align: super;
  }

  .plan-amount {
    font-size: 36px;
    font-weight: 700;
    color: #fff;
    line-height: 1;
  }

  .plan-period {
    font-size: 14px;
    color: #b0b0b0;
  }
}

.plan-btn {
  width: 100%;
  padding: 10px;
  font-weight: 600;
  font-size: 15px;
  border-radius: 8px;

  &.btn-outline-light {
    border-color: #333;
    color: #d0d0d0;

    &:hover {
      background: #1c2225;
      border-color: #00ffd0;
      color: #fff;
    }
  }
}

.disclaimers {
  color: #888;
  font-size: 13px;
}

// Why section
.why-section {
  padding: 80px 0;
}

.why-card {
  display: flex;
  gap: 16px;
  padding: 24px;
  background: #131516;
  border: 1px solid #242424;
  border-radius: 12px;
  height: 100%;
  transition: border-color 0.3s ease;

  &:hover {
    border-color: #333;
  }

  h5 {
    font-size: 18px;
    margin-bottom: 6px;
  }

  p {
    color: #b0b0b0;
    font-size: 15px;
    margin-bottom: 0;
  }
}

.why-icon {
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  border-radius: 10px;
  background: #1c2225;
  display: flex;
  align-items: center;
  justify-content: center;

  i {
    font-size: 20px;
    color: #00ffd0;
  }
}

// Contact section
.contact-section {
  min-height: 100vh;
  padding: 80px 0;
}

.contact-form {
  .form-control {
    padding: 12px 16px;

    &::placeholder {
      color: #555;
    }
  }
}

@media (max-width: 768px) {
  .contact-section {
    min-height: auto;
    padding: 60px 16px;
  }

  .why-section {
    padding: 40px 16px;
  }

  .plans-container {
    padding-left: 16px;
    padding-right: 16px;
  }
}

@media (max-width: 480px) {
  .plan-speed {
    font-size: 26px;
  }

  .plan-amount {
    font-size: 28px !important;
  }
}
</style>
